import { useEffect, useCallback, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UploadDraftList } from '../../components/common/UploadDraftList';
import {
  PageContainer,
  DetailHeader,
  IconButton,
  ChatThread,
  ChatBubble as LayoutChatBubble,
  ChatComposer,
  LoadingState,
  Section,
  List,
  ListRow,
  MetaText,
  PlaceholderQR,
  TiltCard,
  CameraScanner,
} from '../../components/layout';
import { Icon } from '../../components/common/Icon';
import { Toggle } from '../../components/common/Toggle';
import { ChatAttachments } from '../../components/common/ChatAttachments';
import { conversationApi } from '../../api/conversations';
import { useConversationStore, useAgentStore, useAuthStore, toast } from '../../store';
import { ObserverBar } from '../../components/conversation/ObserverBar';
import { useFileUpload } from '../../hooks/useFileUpload';
import { extractErrorMessage } from '../../utils/error';
import type { ConversationParticipantInfo, ConversationMessage } from '../../types';

/**
 * Module-scoped Set of `location.key` values whose `voiceText`
 * payload has already been consumed by a mount of this page.
 * Survives Strict Mode's double-mount because it lives outside
 * any React state — the second mount sees the key already in the
 * set and skips re-adding the message.
 */
const consumedVoiceKeys = new Set<string>();

/**
 * Module-scoped buffer of locally-typed messages for the synthesized
 * default-team chat. Lives outside React state because Strict Mode's
 * double-mount would otherwise wipe React state between mounts and
 * the voice-pickup message would never display. Keyed by a constant
 * since there's only ever one default-team conversation.
 */
const defaultTeamBuffer: { current: ConversationMessage[] } = { current: [] };

/**
 * ConversationDetailPage — the full Alive multi-agent chat surface,
 * built from layout primitives. Mirrors the Gallery `ChatSection`
 * push-navigation pattern: a single frame swaps between six views
 * via a discriminated-union state machine instead of routing.
 *
 *   chat       — the multi-agent conversation view (real messages)
 *   info       — the endpoint detail page (URL / API key / budget /
 *                agents grid / team deps / inference / earnings)
 *   agent      — a single agent's role + abilities + identity in
 *                this team's context
 *   dep        — a dependent team's identity + exposed abilities +
 *                call config + usage stats
 *   abilities  — list of abilities this team publicly exposes
 *   qr         — QR scanner ↔ team QR card (with TiltCard +
 *                CameraScanner)
 *
 * The non-chat views currently render mock data for the Alive-
 * specific fields (endpoint URL, budget, abilities, deps, earnings)
 * because the backend hasn't grown those fields yet. Real
 * participant data flows through from `useConversationStore`.
 */
export function ConversationDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [sending, setSending] = useState(false);
  const { primaryAgentId, myAgents, fetchMyAgents } = useAgentStore();
  // Make sure myAgents is loaded — when the user lands on the
  // default-team page directly (e.g. from a voice-input navigation
  // before the conversations list has had a chance to fetch), the
  // store may still be empty and the synthesized group would only
  // contain the human user. Trigger a fetch on mount.
  useEffect(() => {
    if (myAgents.length === 0) void fetchMyAgents();
  }, [fetchMyAgents, myAgents.length]);
  const currentUser = useAuthStore((s) => s.user);
  const {
    activeConversation, messages, messagesLoading,
    hasMoreMessages, selectConversation, loadMoreMessages, refreshMessages, markRead,
    setLocalConversation,
  } = useConversationStore();

  // Synthesized "默认群" — no backend record. The list page renders
  // a pinned row with this id and the FAB voice overlay broadcasts
  // here. Detail recognizes the id and skips API fetches entirely.
  const DEFAULT_TEAM_ID = 'default-team';
  const isDefaultTeam = id === DEFAULT_TEAM_ID;

  const myAgentId = primaryAgentId || myAgents[0]?.id;
  const myAgent = myAgents.find((a) => a.id === myAgentId);
  // The "mine" bubble should carry the *human user's* avatar, not the
  // agent's. In Alive, agents are personas the user owns; when the
  // human types into the composer, the message represents the human
  // speaking, so we paint their photo on the bubble. Fall back to the
  // primary agent's avatar only if the user has none set.
  const myAvatar = currentUser?.avatar || myAgent?.avatar;

  // ─── Push-style navigation state ────────────────────────────────
  // Six views, exactly like Gallery's ChatSection. The whole frame
  // swaps content based on `view.kind`; back arrows pop one level.
  type ChatView =
    | { kind: 'chat' }
    | { kind: 'info' }
    | { kind: 'agent';     agentId: string }
    | { kind: 'dep';       depId:   string }
    | { kind: 'abilities' }
    | { kind: 'qr';        mode: 'scanner' | 'mine' };
  const [view, setView] = useState<ChatView>({ kind: 'chat' });
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    if (!id) return;
    // eslint-disable-next-line no-console
    console.log('[detail] select effect', { id, isDefaultTeam, myAgentsLen: myAgents.length });
    if (isDefaultTeam) {
      // Synthesize the default-team conversation directly from
      // myAgents — no API call. If myAgents hasn't loaded yet, fall
      // back to a placeholder participant set (using authStore.user)
      // so the page still renders the right header/title instead of
      // bleeding the previous conversation's state through.
      const fallbackParticipants: ConversationParticipantInfo[] = currentUser
        ? [{
            agentId:     currentUser.id,
            agentName:   currentUser.nickname || 'You',
            agentAvatar: currentUser.avatar,
            role:        'creator',
          }]
        : [];
      const participants: ConversationParticipantInfo[] =
        myAgents.length > 0
          ? myAgents.map((a) => ({
              agentId:     a.id,
              agentName:   a.name,
              agentAvatar: a.avatar,
              role:        a.id === myAgentId ? 'creator' : 'member',
            }))
          : fallbackParticipants;
      setLocalConversation({
        id:                 DEFAULT_TEAM_ID,
        type:               'group',
        chatType:           'human-bot',
        title:              t('conversations.defaultTeam', '我和我的 Agents'),
        creatorAgentId:     myAgentId,
        participantCount:   participants.length,
        messageCount:       0,
        lastMessagePreview: '',
        lastMessageAt:      new Date().toISOString(),
        status:             'active',
        createdAt:          new Date().toISOString(),
        participants,
      } as unknown as Parameters<typeof setLocalConversation>[0], myAgentId);
      return;
    }
    selectConversation(id, myAgentId);
  }, [id, myAgentId, isDefaultTeam, myAgents, currentUser, selectConversation, setLocalConversation, t]);

  // Visibility-aware polling so messages stay fresh. Skipped for the
  // default-team since it has no backend record.
  useEffect(() => {
    if (!id || isDefaultTeam) return;
    const refreshIfVisible = () => {
      if (!document.hidden) void refreshMessages(myAgentId);
    };
    refreshIfVisible();
    const interval = window.setInterval(refreshIfVisible, 12000);
    const onVisibilityChange = () => { if (!document.hidden) refreshIfVisible(); };
    const markReadOnFocus = () => {
      if (!document.hidden && id) void markRead(id, myAgentId);
    };
    window.addEventListener('focus', refreshIfVisible);
    window.addEventListener('focus', markReadOnFocus);
    window.addEventListener('online', refreshIfVisible);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('visibilitychange', markReadOnFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshIfVisible);
      window.removeEventListener('focus', markReadOnFocus);
      window.removeEventListener('online', refreshIfVisible);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('visibilitychange', markReadOnFocus);
    };
  }, [id, isDefaultTeam, myAgentId, refreshMessages, markRead]);

  const handleLoadMore = useCallback(() => {
    if (hasMoreMessages && !messagesLoading) loadMoreMessages(myAgentId);
  }, [hasMoreMessages, loadMoreMessages, messagesLoading, myAgentId]);

  const isBotBot = activeConversation?.chatType === 'bot-bot';
  const {
    pendingAttachments,
    setPendingAttachments,
    uploading,
    uploadingPreview,
    fileInputRef,
    pickFile: handlePickFile,
    handleFileChange,
    removeAttachment: removePendingAttachment,
    imageUploadChoiceSheet,
  } = useFileUpload({
    idPrefix: 'upload_conversation',
    disabled: sending || isBotBot,
  });

  const participants: ConversationParticipantInfo[] = activeConversation?.participants || [];
  const otherParticipants = participants.filter((p) => p.agentId !== myAgentId);
  const headerParticipants = activeConversation?.type === 'direct' ? otherParticipants : participants;
  const title = activeConversation?.type === 'group'
    ? activeConversation.title || t('conversations.group')
    : otherParticipants.map((p) => p.agentName).join(' & ') || t('conversations.direct');

  // ─── Default-team local message buffer ────────────────────────
  // Source of truth lives in the *module-scoped* `defaultTeamBuffer`
  // ref so React 18 Strict Mode's double-mount can't wipe it. The
  // component state is just a render-trigger that mirrors the buffer
  // length so React knows when to redraw.
  const [, forceRerender] = useState(0);
  const bumpRerender = useCallback(() => forceRerender((n) => n + 1), []);
  const defaultTeamLocal = isDefaultTeam ? defaultTeamBuffer.current : [];

  // Voice-text hand-off from the FAB long-press flow. TabBar passes
  // the recognized transcript via `location.state.voiceText`. We
  // dedupe via a module-scoped Set keyed on `location.key`, then
  // append directly to the module-scoped buffer (which survives
  // Strict Mode's unmount) and force a re-render so the new bubble
  // appears.
  const voiceTextFromState = (location.state as { voiceText?: string } | null)?.voiceText;
  // eslint-disable-next-line no-console
  console.log('[detail] render', {
    id, isDefaultTeam, locationKey: location.key,
    voiceTextFromState, bufferLen: defaultTeamBuffer.current.length,
  });
  useEffect(() => {
    if (!isDefaultTeam) return;
    if (!voiceTextFromState) return;
    const key = location.key || 'default';
    if (consumedVoiceKeys.has(key)) return;
    consumedVoiceKeys.add(key);
    // eslint-disable-next-line no-console
    console.log('[detail] PUSHING voice message', voiceTextFromState);
    defaultTeamBuffer.current = [
      ...defaultTeamBuffer.current,
      {
        id:              `voice-${Date.now()}`,
        conversationId:  DEFAULT_TEAM_ID,
        senderAgentId:   currentUser?.id ?? 'me',
        senderAgentName: currentUser?.nickname ?? 'You',
        senderAvatar:    currentUser?.avatar,
        content:         voiceTextFromState,
        messageType:     'text',
        createdAt:       new Date().toISOString(),
      },
    ];
    bumpRerender();
    // Auto-reply: every agent in the team greets the user. Staggered
    // by 500ms each so the bubbles arrive one after another, like
    // the network is dispatching to each agent in turn.
    myAgents.forEach((a, i) => {
      setTimeout(() => {
        defaultTeamBuffer.current = [
          ...defaultTeamBuffer.current,
          {
            id:              `auto-${Date.now()}-${a.id}`,
            conversationId:  DEFAULT_TEAM_ID,
            senderAgentId:   a.id,
            senderAgentName: a.name,
            senderAvatar:    a.avatar,
            content:         `早上好，我是 ${a.name}`,
            messageType:     'text',
            createdAt:       new Date().toISOString(),
          },
        ];
        bumpRerender();
      }, 500 * (i + 1));
    });
  }, [isDefaultTeam, voiceTextFromState, location.key, currentUser, myAgents, bumpRerender]);

  // Messages come newest-first from API, reverse for display. The
  // default-team buffer is already oldest-first so it's appended as-is.
  const displayMessages: ConversationMessage[] = isDefaultTeam
    ? defaultTeamLocal
    : [...messages].reverse();

  // Mentionable participants for the composer's @-popup.
  const mentionable = useMemo(
    () => otherParticipants.map((p) => ({
      id:       p.agentId,
      name:     p.agentName,
      avatar:   p.agentAvatar,
      subtitle: p.role,
    })),
    [otherParticipants],
  );

  const handleSendMessage = useCallback(async (text: string, _mentions: string[] = []) => {
    if (!activeConversation || isBotBot) return;
    const trimmed = text.trim();
    if ((!trimmed && pendingAttachments.length === 0) || sending || uploading) return;
    if (isDefaultTeam) {
      // Default team is synthesized client-side — append to the
      // module-scoped buffer (so Strict Mode unmounts don't wipe it)
      // and bump a re-render so the new bubble shows immediately.
      defaultTeamBuffer.current = [
        ...defaultTeamBuffer.current,
        {
          id:              `local-${Date.now()}`,
          conversationId:  DEFAULT_TEAM_ID,
          senderAgentId:   currentUser?.id ?? 'me',
          senderAgentName: currentUser?.nickname ?? 'You',
          senderAvatar:    currentUser?.avatar,
          content:         trimmed,
          messageType:     'text',
          createdAt:       new Date().toISOString(),
        },
      ];
      bumpRerender();
      // Demo auto-reply: every agent in the team responds with a
      // canned greeting. Staggered by 500ms so the bubbles arrive
      // one after another instead of all at once, mimicking the
      // network actually routing the request to each agent in turn.
      myAgents.forEach((a, i) => {
        setTimeout(() => {
          defaultTeamBuffer.current = [
            ...defaultTeamBuffer.current,
            {
              id:              `auto-${Date.now()}-${a.id}`,
              conversationId:  DEFAULT_TEAM_ID,
              senderAgentId:   a.id,
              senderAgentName: a.name,
              senderAvatar:    a.avatar,
              content:         `早上好，我是 ${a.name}`,
              messageType:     'text',
              createdAt:       new Date().toISOString(),
            },
          ];
          bumpRerender();
        }, 500 * (i + 1));
      });
      return;
    }
    setSending(true);
    try {
      await conversationApi.sendMessage(
        activeConversation.id,
        trimmed,
        pendingAttachments.map((item) => item.mediaId),
        myAgentId,
      );
      setPendingAttachments([]);
      await refreshMessages(myAgentId);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('common.sendFailed', 'Send failed')));
    } finally {
      setSending(false);
    }
  }, [activeConversation, isBotBot, isDefaultTeam, currentUser, myAgentId, myAgents, pendingAttachments, refreshMessages, sending, setPendingAttachments, t, uploading, bumpRerender]);

  // ─── Mock fixtures for Alive-specific endpoint metadata ─────────
  // The backend doesn't yet expose endpoint URLs, timer budgets,
  // public ability lists, team dependencies, or earnings — so the
  // info / agent / dep / abilities views use deterministic mock
  // values derived from the conversation id. Swap these for real
  // API data when the endpoints land.
  const endpointHandle = `api.easynet.run/v1/g/${(id || '0000').slice(0, 4)}`;
  const ownerAgentId = activeConversation?.creatorAgentId;

  // Build per-agent mock detail keyed by participant id.
  const agentDetails = useMemo(() => {
    const map = new Map<string, {
      isOwner:        boolean;
      callOrder:      number;
      responseWeight: number;
      abilities:      string[];
      role:           string;
      lifespan:       string;
      status:         string;
    }>();
    participants.forEach((p, i) => {
      map.set(p.agentId, {
        isOwner:        p.agentId === ownerAgentId,
        callOrder:      i + 1,
        responseWeight: 0.9 - i * 0.15,
        abilities:      MOCK_AGENT_ABILITIES[i % MOCK_AGENT_ABILITIES.length],
        role:           p.role || 'member',
        lifespan:       `${50 + i * 30}d`,
        status:         'alive',
      });
    });
    return map;
  }, [participants, ownerAgentId]);

  // ─── Header subtitle for the chat view ──────────────────────────
  const headerSubtitle = (
    <>
      {(activeConversation?.participantCount || participants.length)}{' '}
      {t('conversations.participants', { defaultValue: 'participants' })}
      {isBotBot && (
        <span className="ml-1.5 text-amber-500">· {t('myAgent.observerBadge')}</span>
      )}
    </>
  );

  // ─── Render branch switch ───────────────────────────────────────
  // Same shape as Gallery: nested ternaries that swap the entire
  // page body based on view.kind. Each branch self-contains its
  // header (with appropriate back / actions) and body.
  return (
    <PageContainer
      width="list"
      showTabBar={false}
      bareBody
      header={undefined}
    >
      {/* Hidden file input wired to useFileUpload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="flex flex-col h-full">
        {view.kind === 'chat' ? (
          // ─── Chat view ────────────────────────────────────────
          <>
            <DetailHeader
              title={
                <div className="flex items-center justify-center gap-2 min-w-0">
                  {headerParticipants.length > 0 && (
                    <div className="flex -space-x-2 flex-shrink-0">
                      {headerParticipants.slice(0, 3).map((p, i) => (
                        <div
                          key={i}
                          className="w-7 h-7 rounded-md border-2 border-white dark:border-[#0B0B0D] overflow-hidden bg-gradient-to-br from-primary/25 to-primary/5"
                        >
                          {p.agentAvatar ? (
                            <img src={p.agentAvatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-primary text-[9px] font-bold">{p.agentName.charAt(0)}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <span className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {title}
                  </span>
                </div>
              }
              subtitle={headerSubtitle}
              actions={
                <IconButton
                  icon="more_horiz"
                  label={t('groupInfo.title', 'Endpoint')}
                  onClick={() => setView({ kind: 'info' })}
                />
              }
              onBack={() => navigate(-1)}
            />

            {/* Messages */}
            {messagesLoading && messages.length === 0 ? (
              <LoadingState />
            ) : (
              <ChatThread>
                {hasMoreMessages && (
                  <div className="flex justify-center py-2">
                    <button
                      onClick={handleLoadMore}
                      disabled={messagesLoading}
                      className="text-xs text-primary hover:underline disabled:opacity-50"
                    >
                      {messagesLoading ? '...' : t('conversations.loadMore')}
                    </button>
                  </div>
                )}
                {displayMessages.map((msg) => {
                  if (msg.messageType === 'system') {
                    return (
                      <LayoutChatBubble key={msg.id} variant="system">
                        {msg.content}
                      </LayoutChatBubble>
                    );
                  }
                  // A bubble is "mine" when it was sent by either the
                  // current human user (their messages get the user
                  // avatar) or the user's primary agent. Without the
                  // user-id branch, messages the human typed via the
                  // composer would render on the agents' side.
                  const isMine =
                    msg.senderAgentId === myAgentId ||
                    msg.senderAgentId === currentUser?.id;
                  return (
                    <LayoutChatBubble
                      key={msg.id}
                      variant={isMine ? 'mine' : 'theirs'}
                      senderName={!isMine && activeConversation?.type === 'group' ? msg.senderAgentName : undefined}
                      senderAvatar={isMine ? myAvatar : msg.senderAvatar}
                    >
                      {msg.attachments && msg.attachments.length > 0 && (
                        <ChatAttachments attachments={msg.attachments} />
                      )}
                      {msg.content}
                    </LayoutChatBubble>
                  );
                })}
              </ChatThread>
            )}

            {/* Observer bar — only for bot-bot conversations */}
            {isBotBot && <ObserverBar />}

            {/* Composer — full Gallery-style with mention / voice /
                attach panel. Hidden in bot-bot mode. */}
            {!isBotBot && (
              <>
                {(pendingAttachments.length > 0 || uploading) && (
                  <UploadDraftList
                    className="px-3 pt-2"
                    pendingAttachments={pendingAttachments}
                    uploadingItem={uploadingPreview}
                    onRemoveAttachment={removePendingAttachment}
                    removeTitle={t('common.remove', 'Remove')}
                  />
                )}
                <ChatComposer
                  onSend={(text, mentions) => void handleSendMessage(text, mentions)}
                  placeholder={t('conversations.inputPlaceholder', 'Type a message...')}
                  sendLabel={t('conversations.send', 'Send')}
                  disabled={sending || uploading}
                  mentionable={mentionable}
                  onEmoji={() => {}}
                  voice={{
                    onStart:  () => {},
                    onStop:   () => {},
                    onCancel: () => {},
                  }}
                  attachActions={[
                    { icon: 'photo_library', label: t('conversations.attach.album'),      onClick: handlePickFile },
                    { icon: 'photo_camera',  label: t('conversations.attach.camera'),     onClick: handlePickFile },
                    { icon: 'description',   label: t('conversations.attach.file'),       onClick: handlePickFile },
                    { icon: 'task_alt',      label: t('conversations.attach.assignTask'), onClick: () => {} },
                    { icon: 'analytics',     label: t('conversations.attach.trackUsage'), onClick: () => {} },
                    { icon: 'groups',        label: t('conversations.attach.callTeam'),   onClick: () => {} },
                  ]}
                />
              </>
            )}
          </>
        ) : view.kind === 'info' ? (
          // ─── Endpoint info detail page ────────────────────────
          <>
            <DetailHeader
              title={t('gallery.chat.info.title', 'Endpoint')}
              actions={
                <IconButton
                  icon="qr_code_2"
                  label={t('gallery.chat.qr.shareLabel', 'Share QR')}
                  onClick={() => setView({ kind: 'qr', mode: 'scanner' })}
                />
              }
              onBack={() => setView({ kind: 'chat' })}
            />
            <div className="flex-1 min-h-0 overflow-y-auto">
              {/* Endpoint URL + API key + total timer budget */}
              <Section title={t('gallery.chat.info.endpointSection', 'Endpoint')}>
                <List>
                  <ListRow
                    title={t('gallery.chat.info.endpointUrl', 'URL')}
                    meta={
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 max-w-[180px] truncate">
                          {endpointHandle}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            void navigator.clipboard?.writeText(endpointHandle);
                            toast.success('Copied');
                          }}
                          className="text-[11px] text-primary font-medium hover:underline"
                        >
                          {t('gallery.chat.info.endpointCopy', 'Copy')}
                        </button>
                      </div>
                    }
                  />
                  <ListRow
                    title={t('gallery.chat.info.endpointAuth', 'API key')}
                    meta={<MetaText>{t('gallery.chat.info.endpointAuthValue', 'sk-•••• rotate')}</MetaText>}
                    chevron
                    onClick={() => {}}
                  />
                  <ListRow
                    title={t('gallery.chat.info.totalBudget', 'Total timer budget')}
                    meta={
                      <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                        {t('gallery.chat.info.totalBudgetValue', '12,000 / 20,000 timer')}
                      </span>
                    }
                    chevron
                    onClick={() => {}}
                  />
                </List>
              </Section>

              {/* Member agent grid — WeChat-style 5-col, real
                  participants from the conversation */}
              <Section title={t('gallery.chat.info.agentsSection', 'Agents in this team')}>
                <div className="px-4 py-4">
                  <div className="grid grid-cols-5 gap-3">
                    {participants.map((p) => {
                      const isOwner = agentDetails.get(p.agentId)?.isOwner;
                      return (
                        <button
                          key={p.agentId}
                          type="button"
                          onClick={() => setView({ kind: 'agent', agentId: p.agentId })}
                          className="flex flex-col items-center gap-1 group"
                        >
                          <div className="relative">
                            <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-white/[0.05] group-hover:opacity-80 transition-opacity">
                              {p.agentAvatar ? (
                                <img src={p.agentAvatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/25 to-primary/5">
                                  <span className="text-primary text-sm font-bold">{p.agentName.charAt(0)}</span>
                                </div>
                              )}
                            </div>
                            {isOwner && (
                              <span className="
                                absolute -top-1 -right-1
                                px-1 py-px rounded
                                bg-primary text-white text-[8px] font-bold uppercase tracking-wide
                                ring-2 ring-white dark:ring-[#0B0B0D]
                              ">
                                {t('gallery.chat.agent.owner', 'Owner')}
                              </span>
                            )}
                          </div>
                          <span className={`text-[11px] truncate max-w-full ${isOwner ? 'text-primary font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                            {p.agentName}
                          </span>
                        </button>
                      );
                    })}
                    <button type="button" className="flex flex-col items-center gap-1 group">
                      <div className="
                        w-12 h-12 rounded-md flex items-center justify-center
                        border border-dashed border-gray-300 dark:border-white/[0.15]
                        text-gray-400 dark:text-gray-500
                        group-hover:border-primary/50 group-hover:text-primary
                        transition-colors
                      ">
                        <Icon name="add" size={22} />
                      </div>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        {t('gallery.chat.info.addAgent', 'Add')}
                      </span>
                    </button>
                  </div>
                  <div className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
                    {t('gallery.chat.info.membersCount', { count: participants.length })}
                  </div>
                </div>
              </Section>

              {/* Team dependencies — mock */}
              <Section
                title={t('gallery.chat.info.depsSection', 'Team dependencies')}
                footer={t('gallery.chat.info.depsHint')}
              >
                <List>
                  {MOCK_DEPS.map((dep) => (
                    <ListRow
                      key={dep.id}
                      avatar={
                        <div className="w-9 h-9 rounded-md flex items-center justify-center bg-primary/10 text-primary">
                          <Icon name="groups" size={20} />
                        </div>
                      }
                      title={dep.name}
                      subtitle={
                        <span className="flex items-center gap-1.5">
                          <span className="font-mono truncate">{dep.handle}</span>
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <span>{t('gallery.chat.info.membersCount', { count: dep.members })}</span>
                        </span>
                      }
                      chevron
                      onClick={() => setView({ kind: 'dep', depId: dep.id })}
                    />
                  ))}
                  <ListRow
                    avatar={
                      <div className="w-9 h-9 rounded-md flex items-center justify-center border border-dashed border-gray-300 dark:border-white/[0.15] text-gray-400 dark:text-gray-500">
                        <Icon name="add" size={18} />
                      </div>
                    }
                    title={t('gallery.chat.info.addDep', 'Add dependency')}
                    onClick={() => {}}
                  />
                </List>
              </Section>

              {/* Inference */}
              <Section title={t('gallery.chat.info.paramsSection', 'Inference')}>
                <List>
                  <ListRow
                    title={t('gallery.chat.info.publicAbilities', 'Public abilities')}
                    meta={
                      <MetaText>
                        {t('gallery.chat.info.publicAbilitiesValue', { count: MOCK_PUBLIC_ABILITIES.length })}
                      </MetaText>
                    }
                    chevron
                    onClick={() => setView({ kind: 'abilities' })}
                  />
                  <ListRow
                    title={t('gallery.chat.info.temperature', 'Temperature')}
                    meta={<span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">0.7</span>}
                    chevron
                    onClick={() => {}}
                  />
                  <ListRow
                    title={t('gallery.chat.info.maxTimerPerCall', 'Max timer per call')}
                    meta={<span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">120 timer</span>}
                    chevron
                    onClick={() => {}}
                  />
                </List>
              </Section>

              {/* Webhook */}
              <Section title={t('gallery.chat.info.webhookSection', 'Webhook')}>
                <List>
                  <ListRow
                    title={t('gallery.chat.info.webhookUrl', 'Callback URL')}
                    meta={<MetaText>{t('gallery.chat.info.webhookEmpty', 'None')}</MetaText>}
                    chevron
                    onClick={() => {}}
                  />
                </List>
              </Section>

              {/* Visibility */}
              <Section
                title={t('gallery.chat.info.publicSection', 'Visibility')}
                footer={t('gallery.chat.info.publicHint')}
              >
                <List>
                  <ListRow
                    title={t('gallery.chat.info.public', 'Public endpoint')}
                    meta={<Toggle checked={isPublic} onChange={() => setIsPublic(!isPublic)} />}
                  />
                </List>
              </Section>

              {/* Activity */}
              <Section title={t('gallery.chat.info.logsSection', 'Activity')}>
                <List>
                  <ListRow
                    title={t('gallery.chat.info.logs', 'Recent calls')}
                    meta={<MetaText>{t('gallery.chat.info.logsValue')}</MetaText>}
                    chevron
                    onClick={() => {}}
                  />
                  <ListRow
                    title={t('gallery.chat.info.consumed', 'Timer consumed')}
                    meta={<MetaText>{t('gallery.chat.info.consumedValue')}</MetaText>}
                    chevron
                    onClick={() => {}}
                  />
                </List>
              </Section>

              {/* Earnings */}
              <Section
                title={t('gallery.chat.info.earningsSection', 'Earnings')}
                footer={t('gallery.chat.info.earningsHint')}
              >
                <List>
                  <ListRow
                    title={t('gallery.chat.info.earnedTotal', 'Earned this month')}
                    meta={
                      <span className="font-mono text-[11px] text-emerald-500 font-medium tabular-nums">
                        {t('gallery.chat.info.earnedTotalValue')}
                      </span>
                    }
                    chevron
                    onClick={() => {}}
                  />
                  <ListRow
                    title={t('gallery.chat.info.earnedTopCaller', 'Top caller')}
                    meta={
                      <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[160px]">
                        {t('gallery.chat.info.earnedTopCallerValue')}
                      </span>
                    }
                    chevron
                    onClick={() => {}}
                  />
                  <ListRow
                    title={t('gallery.chat.info.earnedAvgPerCall', 'Avg per call')}
                    meta={
                      <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                        {t('gallery.chat.info.earnedAvgPerCallValue')}
                      </span>
                    }
                  />
                </List>
              </Section>

              {/* Disable + delete */}
              <div className="px-4 mt-6 mb-4 space-y-2">
                <button
                  type="button"
                  onClick={() => {}}
                  className="
                    w-full py-3 rounded
                    bg-gray-50 dark:bg-white/[0.03]
                    text-amber-500 text-sm font-medium
                    hover:bg-gray-100 dark:hover:bg-white/[0.06]
                    transition-colors
                  "
                >
                  {t('gallery.chat.info.disable', 'Disable endpoint')}
                </button>
                <button
                  type="button"
                  onClick={() => setView({ kind: 'chat' })}
                  className="
                    w-full py-3 rounded
                    bg-gray-50 dark:bg-white/[0.03]
                    text-red-500 text-sm font-medium
                    hover:bg-gray-100 dark:hover:bg-white/[0.06]
                    transition-colors
                  "
                >
                  {t('gallery.chat.info.delete', 'Delete team')}
                </button>
              </div>
            </div>
          </>
        ) : view.kind === 'agent' ? (
          // ─── Agent-in-team detail page ────────────────────────
          (() => {
            const agentId = view.agentId;
            const p = participants.find((x) => x.agentId === agentId);
            const detail = agentDetails.get(agentId);
            if (!p || !detail) return null;
            return (
              <>
                <DetailHeader
                  title={p.agentName}
                  subtitle={detail.role}
                  onBack={() => setView({ kind: 'info' })}
                />
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {/* Hero */}
                  <div className="px-4 py-5 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100 dark:bg-white/[0.05] flex-shrink-0">
                      {p.agentAvatar ? (
                        <img src={p.agentAvatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/25 to-primary/5">
                          <span className="text-primary text-xl font-bold">{p.agentName.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {p.agentName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {detail.role}
                      </div>
                      <div className="mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary">
                        {detail.role}
                      </div>
                    </div>
                  </div>

                  {/* Role-in-team */}
                  <Section title={t('gallery.chat.agent.roleSection', 'Role in this team')}>
                    <List>
                      <ListRow
                        title={t('gallery.chat.agent.role', 'Position')}
                        meta={<MetaText>{detail.role}</MetaText>}
                        chevron
                        onClick={() => {}}
                      />
                      <ListRow
                        title={t('gallery.chat.agent.callOrder', 'Call order')}
                        meta={
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            #{detail.callOrder}
                          </span>
                        }
                        chevron
                        onClick={() => {}}
                      />
                      <ListRow
                        title={t('gallery.chat.agent.responseWeight', 'Response weight')}
                        meta={
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            {detail.responseWeight.toFixed(1)}
                          </span>
                        }
                        chevron
                        onClick={() => {}}
                      />
                    </List>
                  </Section>

                  {/* Abilities exposed to this team */}
                  <Section
                    title={t('gallery.chat.agent.abilitiesSection', 'Abilities exposed to this team')}
                    footer={t('gallery.chat.agent.abilitiesHint')}
                  >
                    <List>
                      {detail.abilities.length === 0 ? (
                        <ListRow title={<span className="text-gray-400 dark:text-gray-500 text-sm">{t('gallery.chat.agent.noAbilities')}</span>} />
                      ) : (
                        detail.abilities.map((ability) => (
                          <ListRow
                            key={ability}
                            title={
                              <span className="font-mono text-sm text-primary truncate">
                                {ability}
                              </span>
                            }
                            meta={<Toggle checked onChange={() => {}} />}
                          />
                        ))
                      )}
                    </List>
                  </Section>

                  {/* Identity */}
                  <Section title={t('gallery.chat.agent.identitySection', 'Agent identity')}>
                    <List>
                      <ListRow
                        title={t('gallery.chat.agent.status', 'Status')}
                        meta={<MetaText>{detail.status}</MetaText>}
                      />
                      <ListRow
                        title={t('gallery.chat.agent.lifespan', 'Lifespan')}
                        meta={<MetaText>{detail.lifespan}</MetaText>}
                      />
                      <ListRow
                        title={t('gallery.chat.agent.openProfile', 'Open full profile')}
                        chevron
                        onClick={() => navigate(`/agent/${p.agentId}`)}
                      />
                    </List>
                  </Section>

                  {/* Owner cannot be removed */}
                  {detail.isOwner ? (
                    <div className="px-4 mt-6 mb-4">
                      <div className="
                        px-4 py-3 rounded
                        bg-primary/5 border border-primary/20
                        text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed
                      ">
                        {t('gallery.chat.agent.ownerHint')}
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 mt-6 mb-4">
                      <button
                        type="button"
                        onClick={() => setView({ kind: 'info' })}
                        className="
                          w-full py-3 rounded
                          bg-gray-50 dark:bg-white/[0.03]
                          text-red-500 text-sm font-medium
                          hover:bg-gray-100 dark:hover:bg-white/[0.06]
                          transition-colors
                        "
                      >
                        {t('gallery.chat.agent.remove', 'Remove from team')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            );
          })()
        ) : view.kind === 'dep' ? (
          // ─── Team dependency detail page ──────────────────────
          (() => {
            const dep = MOCK_DEPS.find((d) => d.id === view.depId);
            if (!dep) return null;
            return (
              <>
                <DetailHeader
                  title={dep.name}
                  subtitle={t('gallery.chat.dep.title', 'Team dependency')}
                  onBack={() => setView({ kind: 'info' })}
                />
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <div className="px-4 py-5 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-md flex items-center justify-center bg-primary/10 text-primary flex-shrink-0">
                      <Icon name="groups" size={32} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {dep.name}
                      </div>
                      <div className="font-mono text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {dep.handle}
                      </div>
                    </div>
                  </div>

                  <Section title={t('gallery.chat.dep.identitySection', 'Team identity')}>
                    <List>
                      <ListRow
                        title={t('gallery.chat.dep.depOwner', 'Owner')}
                        meta={<MetaText>{dep.owner}</MetaText>}
                      />
                      <ListRow
                        title={t('gallery.chat.dep.depMembers', 'Members')}
                        meta={
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            {dep.members}
                          </span>
                        }
                      />
                    </List>
                  </Section>

                  <Section
                    title={t('gallery.chat.dep.exposedSection', 'Abilities they expose to us')}
                    footer={t('gallery.chat.dep.exposedHint')}
                  >
                    <List>
                      {dep.exposedAbilities.map((ability) => (
                        <ListRow
                          key={ability}
                          title={
                            <span className="font-mono text-sm text-primary truncate">
                              {ability}
                            </span>
                          }
                          meta={<Toggle checked onChange={() => {}} />}
                        />
                      ))}
                    </List>
                  </Section>

                  <Section title={t('gallery.chat.dep.callConfigSection', 'Call configuration')}>
                    <List>
                      <ListRow
                        title={t('gallery.chat.dep.priority', 'Priority')}
                        meta={
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            P{dep.priority}
                          </span>
                        }
                        chevron
                        onClick={() => {}}
                      />
                      <ListRow
                        title={t('gallery.chat.dep.callOrder', 'Call order')}
                        meta={
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            #{dep.callOrder}
                          </span>
                        }
                        chevron
                        onClick={() => {}}
                      />
                      <ListRow
                        title={t('gallery.chat.dep.timeoutMs', 'Timeout')}
                        meta={
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            {(dep.timeoutMs / 1000).toFixed(0)}s
                          </span>
                        }
                        chevron
                        onClick={() => {}}
                      />
                    </List>
                  </Section>

                  <Section title={t('gallery.chat.dep.usageSection', 'Usage from this team')}>
                    <List>
                      <ListRow
                        title={t('gallery.chat.dep.callsThisMonth', 'Calls this month')}
                        meta={
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            {dep.callsThisMonth.toLocaleString()}
                          </span>
                        }
                      />
                      <ListRow
                        title={t('gallery.chat.dep.timerSpent', 'Timer spent')}
                        meta={
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            {dep.timerSpent.toLocaleString()} timer
                          </span>
                        }
                      />
                      <ListRow
                        title={t('gallery.chat.dep.lastCalled', 'Last called')}
                        meta={<MetaText>{dep.lastCalled}</MetaText>}
                      />
                    </List>
                  </Section>

                  <div className="px-4 mt-6 mb-4 space-y-2">
                    <button
                      type="button"
                      onClick={() => {}}
                      className="w-full py-3 rounded bg-gray-50 dark:bg-white/[0.03] text-primary text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                    >
                      {t('gallery.chat.dep.openTeam', 'Open dependent team')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setView({ kind: 'info' })}
                      className="w-full py-3 rounded bg-gray-50 dark:bg-white/[0.03] text-red-500 text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                    >
                      {t('gallery.chat.dep.remove', 'Remove dependency')}
                    </button>
                  </div>
                </div>
              </>
            );
          })()
        ) : view.kind === 'abilities' ? (
          // ─── Public abilities list ─────────────────────────────
          <>
            <DetailHeader
              title={t('gallery.chat.abilities.title', 'Public abilities')}
              subtitle={t('gallery.chat.abilities.subtitle', 'Exposed by this team')}
              onBack={() => setView({ kind: 'info' })}
            />
            <div className="flex-1 min-h-0 overflow-y-auto">
              <Section footer={t('gallery.chat.abilities.hint')}>
                <List>
                  {MOCK_PUBLIC_ABILITIES.map((ability) => (
                    <ListRow
                      key={ability}
                      title={
                        <span className="font-mono text-sm text-primary truncate">
                          {ability}
                        </span>
                      }
                      subtitle={
                        <span className="font-mono text-[10px] truncate">
                          {endpointHandle}/{ability}
                        </span>
                      }
                      meta={<Toggle checked onChange={() => {}} />}
                    />
                  ))}
                  <ListRow
                    avatar={
                      <div className="w-9 h-9 rounded-md flex items-center justify-center border border-dashed border-gray-300 dark:border-white/[0.15] text-gray-400 dark:text-gray-500">
                        <Icon name="add" size={18} />
                      </div>
                    }
                    title={t('gallery.chat.abilities.addPublic', 'Expose ability')}
                    onClick={() => {}}
                  />
                </List>
              </Section>
            </div>
          </>
        ) : (
          // ─── QR scanner / "my QR" page ─────────────────────────
          (() => {
            const mode = view.mode;
            if (mode === 'scanner') {
              return (
                <div className="flex-1 min-h-0 flex flex-col bg-black text-white relative overflow-hidden">
                  <CameraScanner hint={t('gallery.chat.qr.scanHint', 'Align the QR code within the frame')} />
                  <div className="absolute top-0 left-0 right-0 z-10">
                    <div className="flex items-center px-2 h-12">
                      <button
                        type="button"
                        onClick={() => setView({ kind: 'info' })}
                        aria-label="Back"
                        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/[0.08] transition-colors"
                      >
                        <Icon name="arrow_back_ios" size={18} className="text-white" />
                      </button>
                      <div className="flex-1 text-center text-base font-semibold pr-9">
                        {t('gallery.chat.qr.scanTitle', 'Scan QR')}
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 z-10 pb-6 px-12 flex items-center justify-around">
                    <button
                      type="button"
                      onClick={() => setView({ kind: 'qr', mode: 'mine' })}
                      className="flex flex-col items-center gap-2 group"
                    >
                      <div className="w-12 h-12 rounded-full bg-white/[0.10] hover:bg-white/[0.16] flex items-center justify-center transition-colors backdrop-blur-sm">
                        <Icon name="qr_code_2" size={22} className="text-white" />
                      </div>
                      <span className="text-[11px] text-white/70">{t('gallery.chat.qr.myQr', 'My QR')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {}}
                      className="flex flex-col items-center gap-2 group"
                    >
                      <div className="w-12 h-12 rounded-full bg-white/[0.10] hover:bg-white/[0.16] flex items-center justify-center transition-colors backdrop-blur-sm">
                        <Icon name="image" size={22} className="text-white" />
                      </div>
                      <span className="text-[11px] text-white/70">{t('gallery.chat.qr.album', 'Album')}</span>
                    </button>
                  </div>
                </div>
              );
            }
            // mode === 'mine'
            return (
              <div className="flex-1 min-h-0 flex flex-col bg-black text-white relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 z-10">
                  <div className="flex items-center px-2 h-12">
                    <button
                      type="button"
                      onClick={() => setView({ kind: 'qr', mode: 'scanner' })}
                      aria-label="Back"
                      className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/[0.08] transition-colors"
                    >
                      <Icon name="arrow_back_ios" size={18} className="text-white" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 flex items-center justify-center px-6 pt-12">
                  <div className="w-full max-w-xs">
                    <TiltCard
                      motionLabels={{
                        enable:  t('gallery.chat.qr.tiltEnable', 'Tilt to spin'),
                        pending: t('gallery.chat.qr.tiltPending', 'Requesting…'),
                        denied:  t('gallery.chat.qr.tiltDenied', 'Motion denied'),
                      }}
                    >
                      <div className="rounded-lg bg-white text-gray-900 overflow-hidden shadow-2xl">
                        <div className="relative h-24 bg-gradient-to-br from-sky-200 via-sky-300 to-sky-400">
                          <div className="absolute -bottom-8 left-6">
                            {participants[0]?.agentAvatar ? (
                              <img
                                src={participants[0].agentAvatar}
                                alt=""
                                className="w-16 h-16 rounded-md object-cover border-2 border-white bg-white"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-md border-2 border-white bg-white flex items-center justify-center">
                                <span className="text-primary text-2xl font-bold">{title.charAt(0)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="px-6 pt-10 pb-4">
                          <div className="text-lg font-bold">{title}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {t('gallery.chat.qr.teamLabel', 'Team')}: {(id || '').slice(0, 8)}
                          </div>
                          <div className="text-xs text-gray-600 mt-2 leading-relaxed">
                            {participants.length} agents · OpenAI-compatible endpoint
                          </div>
                        </div>
                        <div className="border-t border-gray-100 px-6 py-4 flex items-end justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="inline-flex items-center px-2 py-0.5 rounded bg-primary text-white text-[10px] font-bold">
                              ALIVE
                            </div>
                            <div className="text-[10px] text-gray-500 mt-2 leading-snug">
                              {t('gallery.chat.qr.scanDescription', 'Scan to add this team as a dependency.')}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <PlaceholderQR url={endpointHandle} />
                          </div>
                        </div>
                      </div>
                    </TiltCard>
                  </div>
                </div>
                <div className="flex-shrink-0 pb-8 px-8 flex items-center justify-around">
                  <button
                    type="button"
                    onClick={() => setView({ kind: 'qr', mode: 'scanner' })}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-12 h-12 rounded-full bg-white/[0.10] hover:bg-white/[0.16] flex items-center justify-center transition-colors">
                      <Icon name="qr_code_scanner" size={22} className="text-white" />
                    </div>
                    <span className="text-[11px] text-white/70">{t('gallery.chat.qr.scan', 'Scan')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {}}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-12 h-12 rounded-full bg-white/[0.10] hover:bg-white/[0.16] flex items-center justify-center transition-colors">
                      <Icon name="download" size={22} className="text-white" />
                    </div>
                    <span className="text-[11px] text-white/70">{t('gallery.chat.qr.save', 'Save')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {}}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-12 h-12 rounded-full bg-white/[0.10] hover:bg-white/[0.16] flex items-center justify-center transition-colors">
                      <Icon name="share" size={22} className="text-white" />
                    </div>
                    <span className="text-[11px] text-white/70">{t('gallery.chat.qr.share', 'Share')}</span>
                  </button>
                </div>
              </div>
            );
          })()
        )}
      </div>

      {imageUploadChoiceSheet}
    </PageContainer>
  );
}

// ─── Mock fixtures (until backend exposes the real fields) ────────

const MOCK_AGENT_ABILITIES: string[][] = [
  ['research.search', 'research.summarize', 'paper.read', 'plan.decompose'],
  ['text.summarize', 'text.rewrite', 'tone.adjust'],
  ['code.generate', 'code.explain', 'review_pr', 'test.write'],
  ['arxiv.scout', 'citation.match'],
];

const MOCK_PUBLIC_ABILITIES = [
  'research.brief',
  'paper.compare',
  'plan.outline',
  'critique.draft',
  'summarize.weekly',
];

interface MockDep {
  id: string;
  name: string;
  handle: string;
  owner: string;
  members: number;
  priority: number;
  callOrder: number;
  timeoutMs: number;
  callsThisMonth: number;
  timerSpent: number;
  lastCalled: string;
  exposedAbilities: string[];
}

const MOCK_DEPS: MockDep[] = [
  {
    id: 'literature-scout',
    name: 'Literature scout crew',
    handle: 'api.easynet.run/v1/g/8a1c',
    owner: '鱼俱罗',
    members: 3,
    priority: 1,
    callOrder: 1,
    timeoutMs: 30000,
    callsThisMonth: 423,
    timerSpent: 1280,
    lastCalled: '12m ago',
    exposedAbilities: ['arxiv.scout', 'paper.fetch', 'citation.match', 'related_work.find'],
  },
  {
    id: 'style-review',
    name: 'Style review crew',
    handle: 'api.easynet.run/v1/g/7e9b',
    owner: '张诗奇',
    members: 2,
    priority: 2,
    callOrder: 2,
    timeoutMs: 15000,
    callsThisMonth: 187,
    timerSpent: 542,
    lastCalled: '2h ago',
    exposedAbilities: ['tone.review', 'grammar.check', 'voice.adjust'],
  },
];
